import { navigate } from "../Layout/Router";
import React from "react";
import { Box, Paper, Chip, Checkbox, FormControlLabel, Typography, TextField, InputAdornment, IconButton, Pagination, Link, ToggleButton, ToggleButtonGroup } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { ChartContainer } from "@mui/x-charts/ChartContainer";
import { BarPlot } from "@mui/x-charts/BarChart";
import { LinePlot, MarkPlot } from "@mui/x-charts/LineChart";
import { ChartsXAxis } from "@mui/x-charts/ChartsXAxis";
import { ChartsYAxis } from "@mui/x-charts/ChartsYAxis";
import { ChartsTooltip } from "@mui/x-charts/ChartsTooltip";
import api from "../Auth/Api";
import globals from "../Globals";
import { PageContainer, SectionHeader, DataTable, StatCard, formatDate } from "../Layout/Common";

const statusChip = {
    valid:    { color: "success", label: "Valid" },
    test:     { color: "info",    label: "Test" },
    promo:    { color: "info",    label: "Promo" },
    pending:  { color: "warning", label: "Pending" },
    revoked:  { color: "error",   label: "Revoked" },
    canceled: { color: "default", label: "Canceled" },
    invalid:  { color: "default", label: "Invalid" },
    fake:     { color: "default", label: "Fake" },
};

// The store a receipt came from (missing on receipts older than the field = Google Play)
const storeChip = {
    google: { label: "Play",      color: "default" },
    apple:  { label: "App Store", color: "default" },
};
const STORES = [{ value: "", label: "All" }, { value: "google", label: "Play" }, { value: "apple", label: "App Store" }];

const usdFormat = (v) => `$${(v ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PAGE_SIZE = 50;
// Explicit column widths + a fixed table layout: long product ids never push the other columns around
const FIXED = { "& table": { tableLayout: "fixed" } };

export default class PurchasesPage extends React.Component {
    state = { dashboard: null, purchases: [], total: 0, includeTest: false, page: 1, search: "", searchInput: "", store: "" };

    componentDidMount() {
        // Sequenced, not parallel: two requests in the same millisecond trip the
        // backend's per-device timestamp anti-replay and the loser gets "retry".
        this.loadDashboard().finally(() => this.loadList(1));
    }

    loadDashboard = () => {
        return api.purchases("Loading dashboard", ["dashboard"], { payload: { includeTest: this.state.includeTest }, silent: true })
            .then(r => { if (r && r.cards) this.setState({ dashboard: r }); })
            .catch(() => {});
    }

    loadList = (page, search = this.state.search, isRetry = false, store = this.state.store) => {
        api.purchases("Loading purchases", ["list"],
            { payload: { skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE, search: search || undefined, store: store || undefined }, silent: true })
            .then(r => {
                if (!r || !r.purchases) return;
                this.setState({ purchases: r.purchases, total: r.total, page, search, store });
            })
            .catch(() => {
                if (!isRetry) setTimeout(() => this.loadList(page, search, true, store), 500);
            });
    }

    setStore = (store) => this.loadList(1, this.state.search, false, store);

    applySearch = () => this.loadList(1, this.state.searchInput.trim());

    clearSearch = () => {
        this.setState({ searchInput: "" });
        this.loadList(1, "");
    }

    toggleIncludeTest = (e) => {
        this.setState({ includeTest: e.target.checked }, this.loadDashboard);
    }

    renderCards() {
        const { dashboard } = this.state;
        if (!dashboard) return null;
        const cards = [
            { label: "Today", data: dashboard.cards.today },
            { label: "Last 7 days", data: dashboard.cards.week },
            { label: "Last 30 days", data: dashboard.cards.month },
            { label: "All time", data: dashboard.cards.allTime },
        ];
        return (
            <>
                <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                    {cards.map(c => (
                        <StatCard key={c.label} label={c.label} value={usdFormat(c.data.usd)}
                                  secondary={`${c.data.sales} sale${c.data.sales === 1 ? "" : "s"}`}
                                  tertiary={c.data.refundCount > 0
                                      ? `−${usdFormat(c.data.refunds)} refunded (${c.data.refundCount})`
                                      : "no refunds"}
                                  tertiaryColor={c.data.refundCount > 0 ? "error.main" : undefined} />
                    ))}
                </Box>
                {dashboard.unpriced > 0 &&
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        {dashboard.unpriced} sale{dashboard.unpriced === 1 ? "" : "s"} awaiting pricing (updated hourly).
                    </Typography>}
                {dashboard.fx &&
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                        USD conversion at each purchase day's rate · rates by{" "}
                        <a href="https://www.exchangerate-api.com" target="_blank" rel="noreferrer" style={{ color: "inherit" }}>exchangerate-api.com</a>
                        {" "}· latest table {dashboard.fx.date}
                    </Typography>}
            </>
        );
    }

    renderChart() {
        const daily = this.state.dashboard?.daily;
        if (!daily || daily.length === 0) return null;
        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Last 30 days</Typography>
                <ChartContainer
                    height={300}
                    xAxis={[{ id: "days", scaleType: "band", data: daily.map(d => d.date.slice(5)) }]}
                    yAxis={[{ id: "sales" }, { id: "usd", position: "right" }]}
                    series={[
                        { type: "bar", id: "sales", yAxisId: "sales", data: daily.map(d => d.sales), label: "Sales" },
                        { type: "line", id: "usd", yAxisId: "usd", data: daily.map(d => d.usd), label: "USD" },
                        { type: "line", id: "refunds", yAxisId: "usd", data: daily.map(d => d.refundsUsd), label: "Refunds", color: "#d32f2f" },
                    ]}
                >
                    <BarPlot />
                    <LinePlot />
                    <MarkPlot />
                    <ChartsXAxis axisId="days" />
                    <ChartsYAxis axisId="sales" />
                    <ChartsYAxis axisId="usd" />
                    <ChartsTooltip />
                </ChartContainer>
            </Paper>
        );
    }

    render() {
        const { purchases, total, includeTest, page, search, searchInput, store } = this.state;
        const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

        const ellipsis = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
        const columns = [
            { id: "date", label: "Date", width: 140, render: p => formatDate(p.date) },
            // Play or App Store; the sandbox / license-tester flag is the Test status, not a separate column
            { id: "store", label: "Store", width: 112, render: p => {
                const chip = storeChip[p.store] ?? storeChip.google;
                return <Chip size="small" variant="outlined" color={chip.color} label={chip.label} title={p.environment ?? p.packageName ?? ""} />;
            }},
            { id: "productId", label: "Product", width: 248, sx: ellipsis, render: p => <span title={p.productId ?? ""}>{p.productId ?? ""}</span> },
            { id: "orderId", label: "Order / transaction", width: 185, sx: ellipsis, render: p => <span title={p.orderId ?? ""}>{p.orderId ?? ""}</span> },
            { id: "amount", label: "Amount", width: 110, align: "right",
                render: p => p.currency ? `${(p.amountMicros / 1000000).toFixed(2)} ${p.currency}` : "—" },
            { id: "usd", label: "USD", width: 90, align: "right", render: p => p.usd != null ? usdFormat(p.usd) : "—" },
            { id: "status", label: "Status", width: 95, render: p => {
                const chip = statusChip[p.status] ?? statusChip.invalid;
                return <Chip size="small" color={chip.color} label={chip.label} />;
            }},
            // A verified player session's context is the purchaser's id: a link to the Players page (titles whose
            // purchases go through the backend flow); a free-form context (LtH) stays text
            { id: "context", label: "Context", width: 200, sx: ellipsis, render: p => p.playerVerified && p.context
                ? <Link component="button" underline="hover" sx={{ fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
                        onClick={() => navigate({ title: globals.selectedTitle, page: "home", sub: "players", deep: [p.context] })}>{p.context}</Link>
                : (p.context ?? "") },
        ];

        return (
            <PageContainer maxWidth={1180}>
                <SectionHeader title="Purchases">
                    <FormControlLabel
                        control={<Checkbox checked={includeTest} onChange={this.toggleIncludeTest} />}
                        label="Include test purchases" />
                </SectionHeader>
                {this.renderCards()}
                {this.renderChart()}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                    <TextField
                        size="small" placeholder="Search order ID, product, context or token…"
                        value={searchInput} sx={{ width: 380 }}
                        onChange={e => this.setState({ searchInput: e.target.value })}
                        onKeyDown={e => { if (e.key === "Enter") this.applySearch(); }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                            endAdornment: (searchInput || search) &&
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={this.clearSearch}><ClearIcon fontSize="small" /></IconButton>
                                </InputAdornment>,
                        }} />
                    <ToggleButtonGroup size="small" exclusive value={store} onChange={(e, v) => v !== null && this.setStore(v)}>
                        {STORES.map(s => <ToggleButton key={s.value} value={s.value} sx={{ whiteSpace: "nowrap" }}>{s.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Typography variant="body2" color="text.secondary">
                        {total} purchase{total === 1 ? "" : "s"}{search ? ` matching “${search}”` : ""}{store ? ` on ${storeChip[store].label}` : ""}
                    </Typography>
                </Box>
                <DataTable columns={columns} rows={purchases} getRowKey={p => p.id} sx={FIXED} />
                {pageCount > 1 &&
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                        <Pagination count={pageCount} page={page} onChange={(e, v) => this.loadList(v)} />
                    </Box>}
            </PageContainer>
        );
    }
}
